'use client'

import {
  FormControl,
  MenuItem,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
} from '@mui/material'
import {
  useState,
  type ChangeEvent,
  useRef,
  useEffect,
  type SyntheticEvent,
} from 'react'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import { Button } from '@/components/library/button'
import { usePaginationContext } from '../utility/pagination/pagination-context'
import { routeCalculatorRelative } from '@/lib/route-calculator'
import { useRouter } from 'next/navigation'
import { useGetRedirectPath } from '@/components/utility/product-filters/product-filters'

interface IRadioFilterProps {
  // eslint-disable-next-line @typescript-eslint/member-delimiter-style
  options: Array<{ label: string; value: string | number }>
  defaultOption?: string | number
  title: string
  selected?: string[] | number[]
  searchTerm?: string
}

export const RadioFilterSmall = ({
  options,
  onFilterSelect,
  selected,
}: {
  options: Array<{
    label: string
    value: string | number
  }>
  onFilterSelect: (selected: string) => void
  selected?: string[] | number[]
}) => (
  <FormControl component='fieldset'>
    <RadioGroup value={selected}>
      {options.map((option) => (
        <FormControlLabel
          key={option.value}
          value={option.value}
          control={<Radio className='p-1' />}
          label={option.label}
          onClick={(e) => {
            onFilterSelect((e.target as HTMLInputElement).value)
          }}
          sx={{
            '& .MuiRadio-root': {
              color: '#713FFF !important',
            },
          }}
        />
      ))}
    </RadioGroup>
  </FormControl>
)

export const RadioFilter = (props: IRadioFilterProps) => {
  const { getRedirectPath } = useGetRedirectPath()
  const { setPage } = usePaginationContext()
  const [selectedOption, setSelectedOption] = useState(
    props.selected ?? props.defaultOption,
  )

  const router = useRouter()

  const handleTypeFilterChange = (selected: string) => {
    if (selected) {
      const suffix = props.searchTerm ? `/${props.searchTerm}` : ''
      const prefix =
        props.searchTerm && selected !== routeCalculatorRelative.search
          ? routeCalculatorRelative.search
          : ''

      router.push(prefix + selected + suffix)
    }
  }

  const handleDiscountFilterChange = (selected: string) => {
    const redirectPath = getRedirectPath(
      selected === '0' ? [] : [selected],
      'discount',
    )
    router.push(redirectPath, {
      scroll: false,
    })
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPage(1)
    setSelectedOption((event.target as HTMLInputElement).value)
    if (props?.title === 'Type') {
      handleTypeFilterChange((event.target as HTMLInputElement).value)
    }
    if (props?.title === 'Discount') {
      handleDiscountFilterChange((event.target as HTMLInputElement).value)
    }
  }

  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event: Event | SyntheticEvent) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return
    }

    setOpen(false)
  }

  function handleListKeyDown(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault()
      setOpen(false)
    } else if (event.key === 'Escape') {
      setOpen(false)
    }
  }
  const prevOpen = useRef(open)
  useEffect(() => {
    if (prevOpen.current && !open) {
      anchorRef.current?.focus()
    }

    prevOpen.current = open
  }, [open])

  return (
    <div>
      <Button
        ref={anchorRef}
        id='radio-filter-button'
        aria-controls={open ? 'radio-filter-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup='true'
        onClick={handleToggle}
        data-testid='filter-title'
        variant='text'
        color='primary'
        size='medium'
        className='ml-0 min-w-16 text-primary-dark no-underline'
      >
        {props?.title}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement='bottom-start'
        style={{ position: 'relative', zIndex: 99 }}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  className='w-max'
                  autoFocusItem={open}
                  id='composition-menu'
                  aria-labelledby='composition-button'
                  onKeyDown={() => handleListKeyDown}
                >
                  <FormControl component='fieldset'>
                    <RadioGroup
                      aria-label='radio-filter-options'
                      name='radio-buttons-group'
                      value={selectedOption}
                      onChange={handleChange}
                    >
                      {props.options.map((option, index) => (
                        <MenuItem
                          key={index}
                          onClick={handleClose}
                          value={option.value}
                        >
                          <FormControlLabel
                            value={option.value}
                            control={<Radio className='p-1' />}
                            label={option.label}
                            sx={{
                              '& .MuiRadio-root': {
                                color: '#713FFF !important',
                              },
                            }}
                          />
                        </MenuItem>
                      ))}
                    </RadioGroup>
                  </FormControl>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  )
}
